// The "?" shortcuts panel and the hint shown after pressing G.
// Sequence rules live in lib/goToShortcuts.ts.

import { useEffect, useId, useRef } from 'react';
import { Keyboard, X } from 'lucide-react';
import { GO_TO, shortcutGroupsFor, type GoToTarget } from '../../lib/goToShortcuts';

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="inline-flex min-w-[1.5rem] items-center justify-center rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-sans text-[11px] font-semibold text-slate-700 shadow-[0_1px_0_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
      {children}
    </kbd>
  );
}

/** Small strip at the bottom of the screen while a G-sequence waits
 *  for its second key, so the letters are discoverable. */
export function GoToHint({ targets = GO_TO }: { targets?: readonly GoToTarget[] }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex justify-center px-4"
    >
      <div className="flex max-w-full flex-wrap items-center justify-center gap-x-3 gap-y-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs text-slate-600 shadow-xl ring-1 ring-slate-900/5 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
        <span className="font-semibold text-foreground">Go to…</span>
        {targets.map((t) => (
          <span key={t.key} className="inline-flex items-center gap-1">
            <Kbd>{t.key.toUpperCase()}</Kbd> {t.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function ShortcutsDialog({ onClose, targets = GO_TO }: { onClose: () => void; targets?: readonly GoToTarget[] }) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
      // The close button is the dialog's only control: keep Tab on it.
      if (e.key === 'Tab') {
        e.preventDefault();
        closeRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center px-4 pt-[12vh]">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-2xl ring-1 ring-slate-900/5 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <Keyboard className="h-4 w-4 text-indigo-500" aria-hidden="true" />
          <h2 id={titleId} className="flex-1 text-sm font-semibold">
            Keyboard shortcuts
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="grid gap-5 p-4 sm:grid-cols-2">
          {shortcutGroupsFor(targets).map((g) => (
            <section key={g.title} aria-label={g.title}>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{g.title}</h3>
              <ul className="space-y-1.5">
                {g.items.map((it) => (
                  <li key={`${it.keys.join('+')}-${it.label}`} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-slate-700 dark:text-slate-300">{it.label}</span>
                    <span className="flex shrink-0 items-center gap-1">
                      {it.keys.map((k, i) => (
                        <Kbd key={i}>{k}</Kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <p className="border-t border-slate-200 px-4 py-2.5 text-xs text-muted-foreground dark:border-slate-700">
          Go-to keys are pressed one after the other: G, then the letter. They don’t work while you’re typing in a field.
        </p>
      </div>
    </div>
  );
}
